import moment from 'moment'

import { util } from './util'
import dayjs from 'dayjs'

/** @typedef {keyof typeof validations} ValidationType */
/** @typedef {(v: any) => boolean | string} ValidationFunction */
/** @typedef {Record<ValidationType, (...args: any[]) => ValidationFunction>} Validations */

const validations = {
  required() {
    return {
      test: (v) => {
        return !!v && ((Array.isArray(v) && v.length > 0) || util.isObject(v) || v.toString().trim().length > 0)
      },
      defaultMessage: 'Campo obrigatório',
    }
  },
  radioRequired() {
    return {
      test: (v) => {
        return v === true || v === false || v?.toString().length > 0
      },
      defaultMessage: 'Campo obrigatório',
    }
  },
  email() {
    return {
      test: (v) =>
        !v ||
        /^(([^<>()[\].,;:\s@"]+(.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
          v,
        ),
      defaultMessage: 'Email inválido',
    }
  },
  phone() {
    return {
      test: (v)=> {
         const expectedFormat = /^\(\d{2}\) 9?\d{4}-\d{4}$/
        return !!v && expectedFormat.test(v)
      },
      defaultMessage: 'Formato inválido',
    }
    
  },
  isSameValueAsOtherField(otherFieldName, otherFieldValue) {
    return {
      test: (v) => !!v && v == otherFieldValue,
      defaultMessage: `O valor deve ser igual ao do campo ${otherFieldName}`,
    }
  },
  validDate() {
    return {
      test: (v) =>
        !v ||
        (v.length === 10 &&
          moment(v, /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(v) ? 'YYYY-MM-DD' : 'DD/MM/YYYY', true).isValid()),
      defaultMessage: 'Data inválida',
    }
  },
  validMonth() {
    return {
      test: (v) => !v || (v.length === 7 && moment(v, 'MM/YYYY', true).isValid()),
      defaultMessage: 'Mês inválido',
    }
  },
  validTime() {
    return {
      test: (v) => !v || (v.length === 5 && moment(v, 'HH:mm', true).isValid()),
      defaultMessage: 'Hora inválida',
    }
  },
  validMobileNumber() {
    return {
      test: (v) => !v || /^\([1-9][0-9]\) [1-9][0-9]{3,4}-[0-9]{4}$/.test(v),
      defaultMessage: 'Número inválido. Verifique se ele está no formato (XX) XXXX-XXXX ou (XX) [9]XXXX-XXXX.',
    }
  },
  validCurrency() {
    return {
      test: (v) =>
        !v || v === '0' || /^[1-9][0-9]*$/.test(v) || /^0?\.[0-9][0-9]$/.test(v) || /^[1-9][0-9]*\.[0-9][0-9]$/.test(v),
      defaultMessage: 'Valor de moeda inválido',
    }
  },
  monthTodayOrBefore() {
    return {
      test: (v) => !v || (v.length === 7 && moment(v, 'MM/YYYY', true).isSameOrBefore(moment(), 'month')),
      defaultMessage: 'Mês precisa ser igual ou anterior ao atual',
    }
  },
  todayOrAfter() {
    return {
      test: (v) => {
        if (!v || v.length !== 10) {
          return true
        }
        const momentDate = moment(v, /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(v) ? 'YYYY-MM-DD' : 'DD/MM/YYYY', true)
        return momentDate.isValid() && momentDate.isSameOrAfter(moment(), 'day')
      },
      defaultMessage: 'Data precisa ser igual ou posterior a hoje',
    }
  },
  dateBefore(referenceDate, canBeEqual = false) {
    referenceDate = referenceDate || moment().format('YYYY-MM-DD')
    return {
      test: (v) => {
        if (!v || v.length !== 10) {
          return true
        }
        const momentReferenceDate = moment(
          referenceDate,
          /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(referenceDate) ? 'YYYY-MM-DD' : 'DD/MM/YYYY',
          true,
        )
        const momentDate = moment(v, /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(v) ? 'YYYY-MM-DD' : 'DD/MM/YYYY', true)

        if (!momentDate.isValid() || !momentReferenceDate.isValid()) {
          return true
        }
        if (canBeEqual) {
          return (
            momentDate.isSameOrBefore(momentReferenceDate, 'day') ||
            `A data deve ser igual ou anterior a ${momentReferenceDate.format('DD/MM/YYYY')}`
          )
        }

        return (
          momentDate.isBefore(momentReferenceDate, 'day') ||
          `A data deve ser anterior a ${momentReferenceDate.format('DD/MM/YYYY')}`
        )
      },
    }
  },
  dateAfter(referenceDate, canBeEqual = false) {
    referenceDate = referenceDate || moment().format('YYYY-MM-DD')
    return {
      test: (v) => {
        if (!v || v.length !== 10) {
          return true
        }
        const momentReferenceDate = moment(
          referenceDate,
          /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(referenceDate) ? 'YYYY-MM-DD' : 'DD/MM/YYYY',
          true,
        )
        const momentDate = moment(v, /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(v) ? 'YYYY-MM-DD' : 'DD/MM/YYYY', true)

        if (!momentDate.isValid() || !momentReferenceDate.isValid()) {
          return true
        }
        if (canBeEqual) {
          return (
            momentDate.isSameOrAfter(momentReferenceDate, 'day') ||
            `A data deve ser igual ou posterior a ${momentReferenceDate.format('DD/MM/YYYY')}`
          )
        }

        return (
          momentDate.isAfter(momentReferenceDate, 'day') ||
          `A data deve ser posterior a ${momentReferenceDate.format('DD/MM/YYYY')}`
        )
      },
    }
  },
  password() {
    return {
      test: (v) => {
        const minPasswordLength = 8
        const validSymbols = ['!', '@', '#', '$', '%', '&', '*', '+', '=', '~', '^', '-', '_']
        const symbolsRegex = new RegExp(`[${validSymbols.join()}]`)
        const numberRegex = /[0-9]/
        const uppercaseLetterRegex = /[A-Z]/
        const letterRegex = /[A-Za-z]/

        if (!v || v.length < minPasswordLength) {
          return `Mínimo de ${minPasswordLength} caracteres`
        }

        v = util.removeAccents(v)

        if (!letterRegex.test(v)) {
          return 'Deve possuir ao menos uma letra'
        }

        if (!uppercaseLetterRegex.test(v)) {
          return 'Deve possuir ao menos uma letra maiúscula'
        }

        if (!numberRegex.test(v)) {
          return 'Deve possuir ao menos um número'
        }

        if (!symbolsRegex.test(v)) {
          return `Deve possuir ao menos um símbolo dentre as opções: ${validSymbols.join(', ')}`
        }

        return true
      },
    }
  },
  cnpj() {
    return {
      test: function (v) {
        if (!v) {
          return true
        }
        v = util.toNumbersOnly(v)
        if (v.length !== 14 || new Set(v.split('')).size === 1) {
          return false
        }

        const calc = (x) => {
          const slice = v.slice(0, x)
          let factor = x - 7
          let sum = 0

          for (let i = x; i >= 1; i--) {
            const n = slice[x - i]
            sum += n * factor--
            if (factor < 2) factor = 9
          }

          const result = 11 - (sum % 11)

          return result > 9 ? 0 : result
        }

        const digits = v.slice(12)

        const digit0 = calc(12)
        if (digit0 != digits[0]) return false

        const digit1 = calc(13)

        return digit1 == digits[1]
      },
      defaultMessage: 'CNPJ inválido',
    }
  },
  cpf() {
    return {
      test: function (v) {
        return isCpfValid(v)
      },
      defaultMessage: 'CPF inválido',
    }
  },
  cpfCnpj() {
    return {
      test: function (v) {
        return validations.cpf.test(v) || validations.cnpj.test(v)
      },
      defaultMessage: 'CPF/CNPJ inválido',
    }
  },
  maxChars(maxChars) {
    return {
      test: (v) => !v || v.length <= maxChars,
      defaultMessage: `Atingido máximo de caracteres`,
    }
  },
  minChars(minChars) {
    return {
      test: (v) => !v || v.length >= minChars,
      defaultMessage: `Não atingiu o mínimo de caracteres`,
    }
  },
  checkUrl() {
    return {
      test: (v) =>
        !!v && /^(https?:\/\/)?([\da-z\.-]+\.[a-z\.]{2,6}|[\d\.]+)([\/:?=&#]{1}[\da-z\.-]+)*[\/\?]?$/gim.test(v),
      defaultMessage: `URL inválida`,
    }
  },

  minValue(minValue, canBeEqual = true) {
    return {
      test: (v) =>
        !v ||
        (canBeEqual
          ? Number(v.toString().replace(',', '.')) >= minValue
          : Number(v.toString().replace(',', '.')) > minValue),
      defaultMessage: `O valor deve ser maior${canBeEqual ? ' ou igual a ' : ' que '}${minValue}`,
    }
  },
  maxValue(maxValue, canBeEqual = true) {
    return {
      test: (v) =>
        !v ||
        (canBeEqual
          ? Number(v.toString().replace(',', '.')) <= maxValue
          : Number(v.toString().replace(',', '.')) < maxValue),
      defaultMessage: `O valor deve ser menor${canBeEqual ? ' ou igual a ' : ' que '}${maxValue}`,
    }
  },
  integer() {
    return {
      test: (v) => !v || !v.toString().includes('.'),
      defaultMessage: `O valor deve ser um número inteiro`,
    }
  },
  thisOrOtherFieldRequired(fieldName, otherFieldName, otherFieldValue) {
    return {
      test: (v) =>
        (!!v && (!Array.isArray(v) || v.length > 0)) ||
        (!!otherFieldValue && (!Array.isArray(otherFieldValue) || otherFieldValue.length > 0)),
      defaultMessage: `${fieldName} ou ${otherFieldName} devem estar preenchidos`,
    }
  },

  validCep() {
    return {
      test: (v) => {
        return !!v && typeof v === 'string' && util.toNumbersOnly(v).toString().trim().length === 8
      },
      defaultMessage: 'Cep deve ter 8 dígitos',
    }
  },
}

const validation = {}
for (const validationName in validations) {
  const validationGenerator = validations[validationName]
  validation[validationName] = (...args) => {
    const validationConfig = validationGenerator(...args)
    return (v) => validationConfig.test(v) || validationConfig.defaultMessage
  }
}

export default {
  install(VueInstance, options) {
    VueInstance.prototype.$validation = validation
  },
}

export function isCpfValid(v) {
  if (!v) {
    return true
  }

  v = util.toNumbersOnly(v)

  if (v.length !== 11 || new Set(v.split('')).size === 1) {
    return false
  }

  let sum = 0
  let remainder = 0
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(v.substring(i - 1, i)) * (11 - i)
  }
  remainder = (sum * 10) % 11

  if (remainder === 10 || remainder === 11) {
    remainder = 0
  }
  if (remainder !== parseInt(v.substring(9, 10))) {
    return false
  }

  sum = 0
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(v.substring(i - 1, i)) * (12 - i)
  }
  remainder = (sum * 10) % 11

  if (remainder === 10 || remainder === 11) {
    remainder = 0
  }

  if (remainder !== parseInt(v.substring(10, 11))) {
    return false
  }

  return true
}
